import type { LegalDoc } from './types'

const UPDATED = 'July 25, 2026'

const TERMS: LegalDoc = {
  title: 'Terms of Service',
  updated: UPDATED,
  intro: [
    'These Terms of Service ("Terms") govern your access to and use of {portal} and related services (the "Service"), operated by {entity} ("we," "us," or "our"). By using the Service you agree to be bound by these Terms. If you do not agree, do not use the Service.',
    '{portal} is a fictional service that appears in a television show. It is a parody. It is not affiliated with, endorsed by, or connected to any real platform it may resemble, and any such resemblance is the point of the joke rather than a claim of association.',
    'Your privacy is governed by our Privacy Policy, which is incorporated into these Terms by reference. Please read both.',
  ],
  sections: [
    {
      heading: '1. Eligibility',
      body: [
        'You must be at least 13 years of age to create an account or use interactive features of the Service. The Service is not directed to children under 13, and we do not knowingly collect personal information from them. If we learn that we have collected personal information from a child under 13, we will delete it and terminate the associated account.',
        'If you are between 13 and the age of majority where you live, you may use the Service only with the involvement and consent of a parent or legal guardian, who agrees to these Terms on your behalf.',
      ],
    },
    {
      heading: '2. The Service Is a Parody',
      body: [
        '{portal} presents itself in the style of a well-known platform for comedic and narrative purposes. Nothing on the Service should be read as a statement about, or on behalf of, the platform it parodies.',
        'Some portals in this family are nonetheless fully functional. Where a portal genuinely takes payment, delivers files, or hosts real projects, those transactions are real and are governed by these Terms notwithstanding the fictional framing.',
      ],
    },
    {
      heading: '3. Your Account',
      body: [
        'Accounts are provided through {entity}\'s shared account system. You are responsible for the accuracy of your registration details, for keeping your credentials secure, and for activity that occurs under your account.',
        'You may close your account at any time. We may suspend or terminate an account that violates these Terms, that is used unlawfully, or where continued operation would expose us or other users to risk.',
      ],
    },
    {
      heading: '4. Acceptable Use',
      body: [
        'Do not use the Service to break the law, to infringe anyone\'s rights, to distribute malware, to attempt to gain unauthorised access to our systems or another user\'s account, or to interfere with the operation of the Service.',
        'Do not use the Service to impersonate a real person or organisation, and do not present material from the Service as though it originated from the platform being parodied.',
        'Automated access, scraping, and load that degrades the Service for others are not permitted without our written consent.',
      ],
    },
    {
      heading: '5. Content You Submit',
      body: [
        'Some portals accept text or files from you. Where a portal states that submissions are not stored, they are not stored: they are processed to produce an immediate response and then discarded.',
        'Where a portal does store what you submit, you keep ownership of it, and you grant {entity} a non-exclusive, worldwide, royalty-free licence to host, reproduce, and display it for the purpose of operating and promoting the Service.',
        'Do not submit anything you lack the rights to submit, and do not submit personal information about other people.',
      ],
    },
    {
      heading: '6. Artificial Intelligence',
      body: [
        'Parts of the Service use AI systems, including to evaluate or respond to material you submit. Our AI Policy explains how and why, and what we do with your input. Where AI is used to respond to you, its output is generated and may be wrong; it is not professional advice of any kind.',
      ],
    },
    {
      heading: '7. Purchases, Pledges, and Entitlements',
      body: [
        'Payments are processed by our payment provider. We do not receive or store your full card details.',
        'Where a portal offers a subscription, it renews on the stated interval until you cancel, and cancellation takes effect at the end of the paid period.',
        'Where a portal offers a pledge toward a project that has not yet shipped, we save your payment method and do not charge it at the time of the pledge. You are charged when the project is ready to be fulfilled, and you will have been told this before pledging. If a project does not proceed, you are not charged.',
        'Digital items and entitlements are licensed to you, not sold, and are non-transferable.',
      ],
    },
    {
      heading: '8. Intellectual Property',
      body: [
        'The Service, its original content, and its arrangement are owned by {entity} and protected by intellectual-property law. Names, marks, and material belonging to any real platform remain the property of their respective owners; our use of a parodic likeness is commentary and does not transfer any right in the original.',
      ],
    },
    {
      heading: '9. Third-Party Services',
      body: [
        'The Service links to and depends on third parties, including payment, hosting, email, and AI providers. Their handling of your data is governed by their own terms and policies, and we are not responsible for third-party content reachable from the Service.',
      ],
    },
    {
      heading: '10. Disclaimers',
      body: [
        'The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, secure, or error-free.',
      ],
    },
    {
      heading: '11. Limitation of Liability',
      body: [
        'To the maximum extent permitted by law, {entity} is not liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, or data, arising from your use of the Service.',
        'To the maximum extent permitted by law, our aggregate liability arising out of or relating to the Service is limited to the greater of the amount you paid us in the twelve months preceding the claim, or one hundred US dollars.',
        'Some jurisdictions do not allow these limitations, in which case they apply to the fullest extent permitted.',
      ],
    },
    {
      heading: '12. Indemnification',
      body: [
        'You agree to indemnify and hold harmless {entity} and its officers, employees, and agents from claims arising out of your use of the Service, your submissions, or your violation of these Terms or of any law or third-party right.',
      ],
    },
    {
      heading: '13. Changes',
      body: [
        'We may modify the Service or these Terms. Where a change is material we will give reasonable notice, and continued use after it takes effect constitutes acceptance. If you do not accept a change, stop using the Service and close your account.',
      ],
    },
    {
      heading: '14. Governing Law and Disputes',
      body: [
        'These Terms are governed by the laws of the State of Texas, without regard to conflict-of-laws rules, and the state and federal courts located in Bexar County, Texas have exclusive jurisdiction, except where applicable law gives you the right to bring a claim elsewhere.',
      ],
    },
    {
      heading: '15. Contact',
      body: ['Questions about these Terms can be sent to legal@gamecodex.com.'],
    },
  ],
}

const PRIVACY: LegalDoc = {
  title: 'Privacy Policy',
  updated: UPDATED,
  intro: [
    'This Privacy Policy explains what {entity} collects when you use {portal}, why, and what control you have. It is written to describe what the software actually does.',
  ],
  sections: [
    {
      heading: '1. What We Collect',
      body: [
        'Account information, if you create an account: your email address, an optional display name, and a securely hashed password. We never store your password itself.',
        'Payment information, if you buy or pledge: handled by our payment processor. We receive a customer reference, the status of the transaction, and the last four digits and brand of a card. We never receive or store your full card number.',
        'Content you submit to a portal, which is handled as described in section 4.',
        'Operational data: request logs, IP address, and browser user-agent, retained for a limited period for security, abuse prevention, and debugging.',
      ],
    },
    {
      heading: '2. What We Do Not Collect',
      body: [
        'We do not sell or share your personal information for cross-context behavioural advertising, and we do not run third-party advertising trackers on the portals.',
        'We do not build advertising profiles about you, and we do not use your submissions to train AI models.',
      ],
    },
    {
      heading: '3. Cookies and Local Storage',
      body: [
        'Signing in stores a session token in your browser\'s local storage so you stay signed in between visits. Signing out removes it. It is not a tracking cookie and it is not readable by any other site.',
        'Some portals store small preferences locally — a dismissed notice, a chosen view. These stay in your browser and are not sent to us.',
        'We do not use third-party advertising or analytics cookies.',
      ],
    },
    {
      heading: '4. Content You Submit, and AI Processing',
      body: [
        'Several portals send what you write to a third-party AI provider in order to generate a response. That transmission is necessary to provide the feature.',
        'Where a portal states that submissions are not stored — for example a portal that evaluates a submission and declines it — the text is sent to the AI provider, used to produce the immediate response, and then discarded. It is not written to our database, and it is not written to our application logs.',
        'Our AI provider processes the text to return a response and does not use it to train its models. It may retain it briefly for abuse monitoring under its own policies.',
        'Do not submit sensitive personal information, credentials, or other people\'s personal information to any portal.',
      ],
    },
    {
      heading: '5. Why We Process It',
      body: [
        'To provide the Service and the features you ask for; to process payments and deliver what you purchased; to secure the Service and prevent abuse; to communicate with you about your account; and to comply with law.',
        'Where the GDPR applies, our bases are performance of a contract, our legitimate interests in operating and securing the Service, your consent where we ask for it, and compliance with legal obligations.',
      ],
    },
    {
      heading: '6. Who We Share It With',
      body: [
        'Service providers who process data on our behalf under contract: hosting, payment processing, transactional email, and AI inference. Each receives only what it needs to perform its function.',
        'Authorities, where we are legally required to disclose, or where disclosure is necessary to protect rights, safety, or the integrity of the Service.',
        'A successor, in connection with a merger, acquisition, or sale of assets, subject to this Policy.',
      ],
    },
    {
      heading: '7. Retention',
      body: [
        'Account data is retained while your account is open and for a limited period afterwards to meet legal, tax, and accounting obligations. Operational logs are retained for a short period and then deleted. Content that a portal states it does not store is never retained at all.',
      ],
    },
    {
      heading: '8. Your Rights',
      body: [
        'Depending on where you live, you may have the right to access, correct, delete, port, or restrict processing of your personal information, and to object to certain processing. Residents of California may request disclosure of the categories collected and may opt out of sale or sharing — we do not sell or share personal information, so there is nothing to opt out of.',
        'You can exercise these rights from your account settings or by writing to privacy@gamecodex.com. We will not discriminate against you for exercising them.',
      ],
    },
    {
      heading: '9. Children',
      body: [
        'The Service is not directed to children under 13 and we do not knowingly collect their personal information. If you believe a child under 13 has provided us information, write to privacy@gamecodex.com and we will delete it.',
      ],
    },
    {
      heading: '10. International Transfers',
      body: [
        'We operate in the United States and our providers may process data there and elsewhere. Where required, transfers out of the EEA or UK rely on Standard Contractual Clauses or another approved mechanism.',
      ],
    },
    {
      heading: '11. Security',
      body: [
        'We use encryption in transit, hashed credentials, scoped service tokens, and least-privilege access. No system is perfectly secure, and we cannot guarantee absolute security.',
      ],
    },
    {
      heading: '12. Changes and Contact',
      body: [
        'We will post any change here and update the date above; material changes will be notified. Questions go to privacy@gamecodex.com.',
      ],
    },
  ],
}

const AI: LegalDoc = {
  title: 'AI Policy',
  updated: UPDATED,
  body: [
    '{portal} is made by humans who apply extensive use of software, algorithms, technology (including artificial intelligence) and physical tools. We use varying AI tools across our work — writing, art, world-building, software, and the systems that run this site.',
    'A human is involved at every step. AI accelerates the work; people set the creative direction, make the decisions, and create the final results before anything reaches you. We know there are many ideologies around how technology infuses the creative process, and we feel we have found the balance that suits us.',
    'Some features respond to you using AI at the moment you use them. Where that is the case the response is generated, may be wrong, and is not advice. It is part of a fictional service and should be read as such.',
    'What we send to an AI provider, and what happens to it afterwards, is described in section 4 of our Privacy Policy. We do not use what you submit to train AI models.',
    'We believe that ambitious inclusion of technologies including AI in our process, guided by human judgment and human creativity at every stage, lets a small team build something deeper and more alive — while keeping it coherent, safe, and true to our creative process.',
  ],
}

/**
 * Exported individually so a portal whose facts differ can override specific
 * sections rather than fork the whole document — DiceTok does this, because it
 * genuinely does store submissions and the shared copy says it doesn't.
 * Overriding beats forking: the other fourteen sections stay in sync.
 */
export { TERMS, PRIVACY, AI as AI_POLICY }

export const LEGAL_DOCS = {
  terms: TERMS,
  privacy: PRIVACY,
  ai: AI,
} satisfies Record<string, LegalDoc>

/** Route/slug keys. Literal rather than `string` so a typo is a build error. */
export type LegalDocKey = keyof typeof LEGAL_DOCS

export const LEGAL_ORDER = ['terms', 'privacy', 'ai'] as const satisfies readonly LegalDocKey[]
